// The R8 recovery message (docs/01-architecture.md section 9), byte for byte
// sdk/rust/src/recovery.rs: one immutable rotation-only Execute per tree,
// leaf 255, NEXT_KEY = derive(t + 1), no aux, no inner instructions, expiry
// u64::MAX. A pure function of (mnemonic, program id, vault, tree index), so
// every device and every restore signs identical bytes. Pinned per tree in
// sdk/fixtures/derivation-v1.json.

import { type Master, deriveTree, signDeterministic, treeKey, wipe } from './derive.js';
import { type ExecHeaderSpec, PUBLIC_KEY_LEN, U64_MAX, executeDigest, executeInstructionData, headerBytes } from './digest.js';

export const RECOVERY_LEAF = 255;
export const RECOVERY_VALID_UNTIL = U64_MAX;

// Vault account layout (program/src/state.rs).
export const VAULT_LEN = 128;
export const TAG_VAULT = 1;
export const TAG_CLOSED = 2;
export const OFF_TAG = 0;
export const OFF_TREE_INDEX = 4;
export const OFF_OPS = 8;
export const OFF_VAULT_ID = 16;
export const OFF_VERIFYING_KEY = 48;
export const OFF_USED_LEAVES = 89;

export type RecoveryErrorCode = 'NotAVault' | 'KeyMismatch' | 'RecoveryLeafUsed';

export class RecoveryError extends Error {
  constructor(readonly code: RecoveryErrorCode, message: string) {
    super(message);
    this.name = 'RecoveryError';
  }
}

export interface VaultSnapshot {
  readonly tag: number;
  readonly treeIndex: number;
  readonly ops: bigint;
  readonly vaultId: Uint8Array;
  readonly verifyingKey: Uint8Array;
  /** 32-byte bitmap, bit `leaf % 8` of byte `leaf / 8`. */
  readonly usedLeaves: Uint8Array;
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i]! ^ b[i]!;
  return d === 0;
}

export function leafUsed(usedLeaves: Uint8Array, leaf: number): boolean {
  return (usedLeaves[leaf >> 3]! & (1 << (leaf & 7))) !== 0;
}

export function usedCount(usedLeaves: Uint8Array): number {
  let n = 0;
  for (const b of usedLeaves) for (let x = b; x; x &= x - 1) n++;
  return n;
}

/** Parse a vault or tombstone account owned by `programId`. */
export function snapshot(programId: Uint8Array, owner: Uint8Array, data: Uint8Array): VaultSnapshot {
  if (!equalBytes(owner, programId) || data.length !== VAULT_LEN || (data[OFF_TAG] !== TAG_VAULT && data[OFF_TAG] !== TAG_CLOSED)) {
    throw new RecoveryError('NotAVault', 'not a vault account of this program');
  }
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  return {
    tag: data[OFF_TAG]!,
    treeIndex: view.getUint32(OFF_TREE_INDEX, true),
    ops: view.getBigUint64(OFF_OPS, true),
    vaultId: data.slice(OFF_VAULT_ID, OFF_VAULT_ID + 32),
    verifyingKey: data.slice(OFF_VERIFYING_KEY, OFF_VERIFYING_KEY + PUBLIC_KEY_LEN),
    usedLeaves: data.slice(OFF_USED_LEAVES, OFF_USED_LEAVES + 32),
  };
}

/** The rotation-only header installing `nextKey`; never expires. */
export function recoveryHeader(nextKey: Uint8Array): ExecHeaderSpec {
  return { numAux: 0, validUntilSlot: RECOVERY_VALID_UNTIL, nextKey };
}

export interface RecoveryMessage {
  readonly treeIndex: number;
  /** root || P of the tree: what the chain must hold for this message to apply. */
  readonly verifyingKey: Uint8Array;
  readonly nextKey: Uint8Array;
  readonly header: ExecHeaderSpec;
  readonly headerBytes: Uint8Array;
  readonly digest: Uint8Array;
  readonly signature: Uint8Array;
  readonly saltTrial: number;
  /** The Execute instruction data; its only account is the vault (writable). */
  readonly instructionData: Uint8Array;
}

/** The successor key a rotation from tree `t` must install (R5): `derive(t + 1)`. */
export function successorKey(master: Master, programId: Uint8Array, t: number): Uint8Array {
  const successor = deriveTree(master, programId, t + 1);
  const key = treeKey(successor).verifyingKey().toBytes();
  wipe(successor.secrets, successor.seed);
  return key;
}

export function recoveryMessage(programId: Uint8Array, vaultAddress: Uint8Array, vaultId: Uint8Array, master: Master, t: number, expectKey?: Uint8Array): RecoveryMessage {
  const current = deriveTree(master, programId, t);
  try {
    const key = treeKey(current);
    const verifyingKey = key.verifyingKey().toBytes();
    if (expectKey && !equalBytes(expectKey, verifyingKey)) throw new RecoveryError('KeyMismatch', `the chain's key is not derive(${t}) of this mnemonic`);
    const nextKey = successorKey(master, programId, t);
    const header = recoveryHeader(nextKey);
    const digest = executeDigest({ programId, vaultId, vaultAddress, treeIndex: t, leaf: RECOVERY_LEAF, header, aux: [], inners: [] });
    const { signature, saltTrial } = signDeterministic(key, current.seed, RECOVERY_LEAF, digest);
    const sig = signature.toBytes();
    return { treeIndex: t, verifyingKey, nextKey, header, headerBytes: headerBytes(header), digest, signature: sig, saltTrial, instructionData: executeInstructionData(sig, header, []) };
  } finally {
    wipe(current.secrets, current.seed);
  }
}

/** The recovery message for a vault (or tombstone) as it stands on chain: the derived key must match (R5) and leaf 255 must be free. */
export function recoveryForAccount(programId: Uint8Array, vaultAddress: Uint8Array, owner: Uint8Array, data: Uint8Array, master: Master): RecoveryMessage {
  const s = snapshot(programId, owner, data);
  if (leafUsed(s.usedLeaves, RECOVERY_LEAF)) throw new RecoveryError('RecoveryLeafUsed', 'leaf 255 of the current tree is spent');
  return recoveryMessage(programId, vaultAddress, s.vaultId, master, s.treeIndex, s.verifyingKey);
}
